import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useMs365Onboarding } from "@/hooks/use-ms365-onboarding";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { CheckCircle2, AlertCircle, Loader2, Copy, ExternalLink, Mail } from "lucide-react";

function copyToClipboard(text: string | null): Promise<boolean> {
  if (!text) return Promise.resolve(false);
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  return Promise.resolve(false);
}

export function Ms365OnboardingPage() {
  const { t } = useTranslation();
  const { status, data, error, refresh } = useMs365Onboarding();
  const [copied, setCopied] = useState(false);
  const [copiedUser, setCopiedUser] = useState(false);

  const handleCopyLink = async () => {
    if (data?.user_auth_url) {
      const ok = await copyToClipboard(data.user_auth_url);
      if (ok) {
        setCopiedUser(true);
        setTimeout(() => setCopiedUser(false), 2000);
      }
    }
  };

  const isLoading = status === "loading";
  const isInvalid = status === "token_invalid" || status === "token_expired";
  const adminDone = status === "admin_consent_done" || status === "fully_complete";
  const fullyComplete = status === "fully_complete";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-foreground">{t("connections:ms365Onboarding.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data?.customer_name ?? t("connections:ms365Onboarding.yourOrganization")}
          </p>
        </div>

        {isLoading && (
          <Card className="p-8">
            <div className="flex flex-col items-center space-y-3 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 dark:text-blue-400" />
              <p className="text-sm text-muted-foreground">{t("connections:ms365Onboarding.loading")}</p>
            </div>
          </Card>
        )}

        {isInvalid && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("connections:ms365Onboarding.invalidTitle")}</AlertTitle>
            <AlertDescription>
              <Trans
                i18nKey="connections:ms365Onboarding.invalidDescription"
                components={{ support: <a href="mailto:support@layer-nine.ai" className="underline" /> }}
              />
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isInvalid && (
          <Card className="space-y-6 p-6">
            {/* Step 1: Admin consent */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                  1
                </div>
                <h2 className="font-medium text-foreground">{t("connections:ms365Onboarding.grantConsent")}</h2>
                {adminDone && <CheckCircle2 className="h-4 w-4 text-green-500" />}
              </div>
              <p className="pl-8 text-sm text-muted-foreground">
                {t("connections:ms365Onboarding.grantConsentDescription")}
              </p>
              <div className="pl-8 space-y-3">
                {data?.requested_scopes && data.requested_scopes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {t("connections:ms365Onboarding.selectedPermissions")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {data.requested_scopes.map((scope) => (
                        <Badge key={scope} variant="outline" className="font-normal">
                          {scope}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {adminDone ? (
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">{t("connections:ms365Onboarding.consentGranted")}</p>
                ) : data?.admin_consent_url ? (
                  <Button
                    onClick={() => { window.location.href = data.admin_consent_url!; }}
                    className="w-full sm:w-auto"
                  >
                    {t("connections:ms365Onboarding.grantConsent")}
                    <ExternalLink className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <p className="text-sm text-amber-600 dark:text-amber-400">{t("connections:ms365Onboarding.consentUrlUnavailable")}</p>
                )}
              </div>
            </div>

            {/* Step 2: Allow users */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                  2
                </div>
                <h2 className="font-medium text-foreground">{t("connections:ms365Onboarding.allowAllUsers")}</h2>
              </div>
              <p className="pl-8 text-sm text-muted-foreground">
                <Trans
                  i18nKey="connections:ms365Onboarding.allowAllUsersDescription"
                  components={{ app: <strong />, setting: <em />, value: <strong /> }}
                />
              </p>
              <div className="pl-8">
                <Button
                  variant="outline"
                  onClick={() => { window.open("https://portal.azure.com/#blade/Microsoft_AAD_IAM/StartboardApplicationsMenuBlade", "_blank"); }}
                >
                  {t("connections:ms365Onboarding.openAzurePortal")}
                  <ExternalLink className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Step 3: Forward user auth link */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                  3
                </div>
                <h2 className="font-medium text-foreground">{t("connections:ms365Onboarding.forwardUserLink")}</h2>
              </div>
              <p className="pl-8 text-sm text-muted-foreground">
                {t("connections:ms365Onboarding.forwardUserLinkDescription")}
              </p>
              <div className="pl-8">
                {data?.user_auth_url ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCopyLink}
                      className="flex-1"
                    >
                      <Copy className="mr-1 h-4 w-4" />
                      {copiedUser
                        ? t("connections:ms365Onboarding.copied")
                        : t("connections:ms365Onboarding.copyUserLink")}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-amber-600 dark:text-amber-400">{t("connections:ms365Onboarding.userLinkUnavailable")}</p>
                )}
              </div>
            </div>

            {/* Status summary */}
            {fullyComplete && (
              <div className="rounded-md bg-green-50 p-4 text-sm text-green-800 dark:bg-green-950/50 dark:text-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-medium">{t("connections:ms365Onboarding.complete")}</span>
                </div>
                <p className="mt-1 pl-6">
                  {t("connections:ms365Onboarding.completeDescription", {
                    count: data?.active_connections ?? 0,
                  })}
                </p>
              </div>
            )}

            {adminDone && !fullyComplete && (
              <div className="rounded-md bg-blue-50 p-4 text-sm text-blue-800 dark:bg-blue-950/50 dark:text-blue-200">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span className="font-medium">{t("connections:ms365Onboarding.nextStep")}</span>
                </div>
                <p className="mt-1 pl-6">
                  {t("connections:ms365Onboarding.nextStepDescription")}
                </p>
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground">
              {t("connections:ms365Onboarding.needHelp")} {" "}
              <a href="mailto:support@layer-nine.ai" className="underline">
                support@layer-nine.ai
              </a>
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
